import store from '@/store'
import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // For Refreshing Token
  subscribers = []

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        store.commit('api/SET_API_CALL_IN_PROGRESS', true)
        // Get token from localStorage
        const accessToken = this.getToken()

        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => {
        store.commit('api/SET_API_CALL_IN_PROGRESS', false)
        return response
      },
      error => {
        // const { config, response: { status } } = error
        const { config, response } = error
        const originalRequest = config

        if (response && response.status === 401) {
          // DESLOGANDO O USUÁRIO
          localStorage.clear()
          window.location = '/'

          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true
            this.refreshToken().then(r => {
              this.isAlreadyFetchingAccessToken = false

              // Update accessToken in localStorage
              this.setToken(r.data.accessToken)
              this.setRefreshToken(r.data.refreshToken)

              this.onAccessTokenFetched(r.data.accessToken)
            })
          }
          const retryOriginalRequest = new Promise(resolve => {
            this.addSubscriber(accessToken => {
              // Make sure to assign accessToken according to your response.
              // Check: https://pixinvent.ticksy.com/ticket/2413870
              // Change Authorization header
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
              resolve(this.axiosIns(originalRequest))
            })
          })
          return retryOriginalRequest
        }

        if (response && response.status === 400) {
          store.commit('api/SET_API_CALL_IN_PROGRESS', false)
        }

        if (!response) {
          store.commit('api/TOGGLE_MODAL_ERRO_500', true)

          setTimeout(() => {
            store.commit('api/TOGGLE_MODAL_ERRO_500', false)
          }, 6000)
        }

        return Promise.reject(error)
      },
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  changePassword(...args) {
    return this.axiosIns.post(this.jwtConfig.changePassword, ...args)
  }

  recoveryPassword(...args) {
    return this.axiosIns.post(this.jwtConfig.passwordRecovery, ...args)
  }

  changePasswordRecovery(...args) {
    return this.axiosIns.post(this.jwtConfig.changePasswordRecovery, ...args)
  }

  updateAccount(...args) {
    return this.axiosIns.post(this.jwtConfig.updateUserAccount, ...args)
  }

  validateAccount(token) {
    return this.axiosIns.post(`${this.jwtConfig.validadeAccount}/${token}`)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken(),
    })
  }

  // Campaing
  listCampaing() {
    return this.axiosIns.get(this.jwtConfig.listCampaing)
  }

  getCampaingByToken(token) {
    return this.axiosIns.get(`${this.jwtConfig.getCampaingByToken}/${token}`)
  }

  updateStatusCampaing(token, status) {
    return this.axiosIns.put(`${this.jwtConfig.updateStatusCampaing}/${token}/${status}`)
  }

  updateSalesButton(token, ...args) {
    return this.axiosIns.put(`${this.jwtConfig.updateSalesButton}/${token}`, ...args)
  }

  createCampaing(...args) {
    return this.axiosIns.post(this.jwtConfig.createCampaing, ...args)
  }

  // Indication
  listIndication(...args) {
    return this.axiosIns.get(this.jwtConfig.indication, ...args)
  }

  detailsCoupon(couponToken) {
    return this.axiosIns.get(`${this.jwtConfig.detailsCoupon}/${couponToken}`)
  }

  // Validate
  validateCoupon(voucherCode) {
    return this.axiosIns.get(`${this.jwtConfig.validateCoupon}/${voucherCode}`)
  }

  createCoupon(...args) {
    return this.axiosIns.post(this.jwtConfig.createCoupon, ...args)
  }

  checkoutCoupon(...args) {
    return this.axiosIns.post(this.jwtConfig.checkoutCoupon, ...args)
  }

  cancelateCoupon(couponToken) {
    return this.axiosIns.put(`${this.jwtConfig.cancelateCoupon}/${couponToken}`)
  }

  // Rewards
  listPayments(...args) {
    return this.axiosIns.get(this.jwtConfig.payments, ...args)
  }

  payReward(paymentToken) {
    return this.axiosIns.get(`${this.jwtConfig.pay}/${paymentToken}`)
  }

  listVoucher(...args) {
    return this.axiosIns.get(this.jwtConfig.voucher, ...args)
  }

  // Dashboard
  statisticsItems(...args) {
    return this.axiosIns.post(this.jwtConfig.statisticsItems, ...args)
  }

  dashboardUsersData(...args) {
    return this.axiosIns.post(this.jwtConfig.dashboardUsersData, ...args)
  }

  dashboardConversion(...args) {
    return this.axiosIns.post(this.jwtConfig.dashboardConversion, ...args)
  }

  tableOfCampaingConversionDashboard(...args) {
    return this.axiosIns.post(this.jwtConfig.tableOfCampaingConversionDashboard, ...args)
  }

  listCampaingInDashboard() {
    return this.axiosIns.get(this.jwtConfig.listCampaingInDashboard)
  }

  // Profile
  createProfile(...args) {
    return this.axiosIns.post(this.jwtConfig.createProfile, ...args)
  }

  listProfile() {
    return this.axiosIns.post(this.jwtConfig.listProfile)
  }

  getListFeatures() {
    return this.axiosIns.post(this.jwtConfig.getListFeatures)
  }

  updateProfile(...args) {
    return this.axiosIns.post(this.jwtConfig.updateProfile, ...args)
  }

  deleteProfile(...args) {
    return this.axiosIns.post(this.jwtConfig.deleteProfile, ...args)
  }

  // User
  createUser(...args) {
    return this.axiosIns.post(this.jwtConfig.createUser, ...args)
  }

  listUsers(...args) {
    return this.axiosIns.post(this.jwtConfig.listUsers, ...args)
  }

  deleteUser(...args) {
    return this.axiosIns.post(this.jwtConfig.deleteUser, ...args)
  }

  getUserSystem(...args) {
    return this.axiosIns.post(this.jwtConfig.getUserSystem, ...args)
  }

  updateUserSystem(...args) {
    return this.axiosIns.post(this.jwtConfig.updateUserSystem, ...args)
  }

  // financials
  getAllRangesIndications() {
    return this.axiosIns.get(this.jwtConfig.getAllRangesIndications)
  }

  getActivePlan() {
    return this.axiosIns.get(this.jwtConfig.companyActivePlan)
  }

  setAssociatePlan(...args) {
    return this.axiosIns.post(this.jwtConfig.associatePlan, ...args)
  }

  //  payments
  createPayment(...args) {
    return this.axiosIns.post(this.jwtConfig.createPayment, ...args)
  }

  checkCompanyPaymentMethods() {
    return this.axiosIns.post(this.jwtConfig.CheckCompanyPaymentStatus)
  }

  listPaymentsMethods() {
    return this.axiosIns.get(this.jwtConfig.ListPaymentMethods)
  }

  deletePaymentMethod(methodId) {
    return this.axiosIns.post(`${this.jwtConfig.DeletePaymentMethods}/${methodId}`)
  }

  setDefaultPaymentMethod(methodId) {
    return this.axiosIns.post(`${this.jwtConfig.setPaymentDefault}/${methodId}`)
  }

  addPaymentMethod(...args) {
    return this.axiosIns.post(this.jwtConfig.AddPaymentMethod, ...args)
  }

  alterRecurringPayment(...args) {
    return this.axiosIns.post(this.jwtConfig.alterRecurringPayment, ...args)
  }

  updateCurrentPlan(...args) {
    return this.axiosIns.post(this.jwtConfig.updateCurrentPlan, ...args)
  }

  historyCompanyPlans(...args) {
    return this.axiosIns.post(this.jwtConfig.historyCompanyPlans, ...args)
  }

  // company & app configs
  getCompanyInfo() {
    return this.axiosIns.get(this.jwtConfig.getCompany)
  }

  getRewardsSetting() {
    return this.axiosIns.get(this.jwtConfig.getSettingsRewards)
  }

  updateRewardsSetting(...args) {
    return this.axiosIns.put(this.jwtConfig.updateSettingRewards, ...args)
  }

  updateInfoCompany(...args) {
    return this.axiosIns.put(this.jwtConfig.updateInfoCompany, ...args)
  }

  updateLogoCompany(...args) {
    return this.axiosIns.post(this.jwtConfig.updateLogoCompany, ...args)
  }

  updateColorsCompany(...args) {
    return this.axiosIns.post(this.jwtConfig.updateColorsCompany, ...args)
  }
}
